/*
  MIGRATION_DEBT: bespoke form-input + button styling, and
  var(--token, #hex) fallback patterns matching GmailComposeIsland.

  See the MIGRATION_DEBT header in GmailComposeIsland.tsx for the
  rationale and removal condition. Both files are deferred to the
  Phase 5 Modal/Dialog migration; piecemeal cleanup here is
  explicitly out of scope until then.
*/

import { useEffect, useRef, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { getActiveWorkspaceId } from '../../lib/workspace'
import type { GmailComposeContext, IslandProps } from '../registry'

interface Props {
  user: NonNullable<IslandProps['user']>
  ctx: GmailComposeContext
  accountEmail: string
  signature: string
  onCancel: () => void
  onSent: () => void
}

type SendState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'error'; message: string }
  | { kind: 'sent' }

/**
 * Plain-text compose form. V1 constraints:
 *   - Single recipient (the contact); To: locked.
 *   - No CC/BCC, no attachments, no HTML editor.
 *   - Signature is read-only here (edited in ⚙ Settings).
 *
 * The Send button is disabled until both subject and body are non-empty.
 * On send: POST to /functions/v1/gmail-send. The function does its own
 * gmail_connections lookup, token refresh, RFC 2822 build, Gmail API
 * call, and gmail_threads/gmail_messages persistence. We just surface
 * its response to the user.
 */
export function ComposeForm({ user, ctx, accountEmail, signature, onCancel, onSent }: Props) {
  const [subject, setSubject] = useState(initialSubject(ctx))
  const [body, setBody] = useState('')
  const [state, setState] = useState<SendState>({ kind: 'idle' })
  const bodyRef = useRef<HTMLTextAreaElement | null>(null)

  // Focus the body field on mount so the rep can start typing immediately.
  // The replyTo flow wants the cursor in the body too (subject is pre-filled).
  useEffect(() => {
    const t = setTimeout(() => bodyRef.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [])

  const canSend = state.kind !== 'sending' && subject.trim().length > 0 && body.trim().length > 0

  async function handleSend() {
    if (!canSend) return
    setState({ kind: 'sending' })

    try {
      const { data: sess } = await supabase.auth.getSession()
      const accessToken = sess?.session?.access_token
      if (!accessToken) {
        setState({ kind: 'error', message: 'Sign in expired. Refresh the page and try again.' })
        return
      }

      // Resolve the supabase function endpoint. supabase-js v2 exposes
      // the URL via supabase.functions.url; we read SUPABASE_URL from
      // the configured client via a small probe to keep this island
      // independent of any env layer.
      const fnUrl =
        (supabase as unknown as { functionsUrl?: string })?.functionsUrl ?? buildFunctionsUrl()
      const sendUrl = `${fnUrl}/gmail-send`

      const reqBody: Record<string, unknown> = {
        contact_id: ctx.contactId,
        subject: subject.trim(),
        body,
      }
      if (ctx.replyTo) {
        reqBody.in_reply_to = ctx.replyTo.gmailMessageId
        reqBody.thread_id = ctx.replyTo.gmailThreadId
      }

      const res = await fetch(sendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...(getActiveWorkspaceId() ? { 'x-workspace-id': getActiveWorkspaceId() as string } : {}),
        },
        body: JSON.stringify(reqBody),
      })
      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean
        error?: string
        warning?: string
        detail?: string
        gmail_message_id?: string
        gmail_thread_id?: string
      }

      if (!res.ok || json.ok === false) {
        const msg = json.error || json.detail || `Gmail send failed (HTTP ${res.status})`
        setState({ kind: 'error', message: msg })
        return
      }

      // Success — even when there's a soft warning (e.g. local insert
      // failed after Gmail accepted the send) we count it as sent for
      // the user.
      setState({ kind: 'sent' })

      // Toast the warning so the user knows logging will catch up on
      // the next sync.
      if (json.warning) {
        notifyHost('warn', json.warning)
      } else {
        notifyHost('success', 'Email sent ✓')
      }

      // Brief delay so the user sees "Sent ✓" before the modal closes.
      setTimeout(onSent, 350)
    } catch (e) {
      setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) })
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ padding: '14px 18px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Field label="To">
          <div
            style={{
              fontSize: 13,
              padding: '8px 10px',
              background: 'var(--bg-muted, #f3f4f6)',
              borderRadius: 6,
              color: 'var(--text, #111827)',
            }}
          >
            {recipientLabel(ctx)}
          </div>
        </Field>

        <Field label="From">
          <div
            style={{
              fontSize: 13,
              padding: '8px 10px',
              background: 'var(--bg-muted, #f3f4f6)',
              borderRadius: 6,
              color: 'var(--text-muted, #6b7280)',
            }}
          >
            {accountEmail || '(Gmail not connected)'}
          </div>
        </Field>

        <Field label="Subject">
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={state.kind === 'sending' || state.kind === 'sent'}
            placeholder="Subject"
            style={inputStyle}
          />
        </Field>

        <Field label="Body">
          <textarea
            ref={bodyRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={state.kind === 'sending' || state.kind === 'sent'}
            placeholder="Write your email — plain text only, signature appended automatically"
            rows={10}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 160, fontFamily: 'inherit' }}
          />
        </Field>

        {signature.trim().length > 0 && (
          <div>
            <div style={labelStyle}>Signature (from ⚙ Settings)</div>
            <pre
              style={{
                margin: 0,
                padding: '8px 10px',
                fontSize: 12,
                fontFamily:
                  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                background: 'var(--bg-muted, #f3f4f6)',
                color: 'var(--text-muted, #6b7280)',
                borderRadius: 6,
                whiteSpace: 'pre-wrap',
              }}
            >
              {`-- \n${signature}`}
            </pre>
          </div>
        )}

        {state.kind === 'error' && (
          <div
            style={{
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.4)',
              color: 'var(--red, #dc2626)',
              padding: '8px 10px',
              borderRadius: 6,
              fontSize: 13,
              whiteSpace: 'pre-wrap',
            }}
          >
            {state.message}
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          padding: 18,
          borderTop: '1px solid var(--border, #e5e7eb)',
          marginTop: 14,
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={state.kind === 'sending'}
          style={secondaryButtonStyle}
        >
          {state.kind === 'sent' ? 'Close' : 'Cancel'}
        </button>
        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={!canSend}
          style={{
            ...primaryButtonStyle,
            opacity: canSend ? 1 : 0.5,
            cursor: canSend ? 'pointer' : 'not-allowed',
          }}
        >
          {state.kind === 'sending' ? 'Sending…' : state.kind === 'sent' ? 'Sent ✓' : 'Send →'}
        </button>
      </div>
      {/* user param is part of the signature for future telemetry / per-user signature etc. */}
      <span style={{ display: 'none' }} data-user-id={user.id} />
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      {children}
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-muted, #6b7280)',
  marginBottom: 4,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  fontSize: 13,
  fontFamily: 'inherit',
  background: 'var(--bg, #ffffff)',
  color: 'var(--text, #111827)',
  border: '1px solid var(--border, #e5e7eb)',
  borderRadius: 6,
  outline: 'none',
}

const primaryButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  background: 'var(--accent, #111827)',
  color: 'var(--accent-fg, #ffffff)',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
}

const secondaryButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: 13,
  background: 'transparent',
  color: 'var(--text, #111827)',
  border: '1px solid var(--border, #e5e7eb)',
  borderRadius: 6,
  cursor: 'pointer',
}

function recipientLabel(ctx: GmailComposeContext): string {
  if (ctx.contactName) return `${ctx.contactName} <${ctx.contactEmail}>`
  return ctx.contactEmail
}

function initialSubject(ctx: GmailComposeContext): string {
  if (ctx.replyTo) {
    const original = ctx.replyTo.originalSubject ?? ''
    return /^re:\s/i.test(original) ? original : `Re: ${original}`
  }
  return ''
}

function buildFunctionsUrl(): string {
  // supabase-js doesn't always expose the resolved functions URL on the
  // client surface across versions. The legacy host page hardcodes
  // SB_URL = 'https://ouyzishoamkmehmqlnry.supabase.co'; mirroring that.
  return 'https://ouyzishoamkmehmqlnry.supabase.co/functions/v1'
}

function notifyHost(level: 'success' | 'warn', message: string) {
  // The legacy host listens for toast() via direct calls; we don't have
  // a wired event bus for toasts yet, so dispatch a custom event the
  // host can wire up if it wants. As a graceful fallback, log.
  try {
    window.dispatchEvent(new CustomEvent('gmail-compose-toast', { detail: { level, message } }))
  } catch {
    // ignore
  }
  if (level === 'warn') console.warn(message)
}
